/**
 * 🔴 **SIGNING IN WITH A PASSKEY** (PNK-137) — the shell's side of the ceremony.
 *
 * Devin: *"once you have setup passwords, then setup passkeys and finally SSO."*
 *
 * A passkey is a key pair whose private half never leaves the authenticator — the
 * phone's secure enclave, the laptop's TPM, the hardware key. Signing in means the
 * server sends a challenge and the authenticator returns a signature over it. So
 * there is nothing to type, nothing to remember, nothing to reuse on another site,
 * and nothing in the database that opens an account if it leaks.
 *
 * 🔴 **THE BROWSER, NOT THIS CODE, IS WHAT MAKES IT UNPHISHABLE.** A credential is
 * bound to the relying-party id it was created for (`pinksy.net`), and the browser
 * refuses to offer it to any other origin. There is no code to read out to somebody
 * on the telephone and no field on a lookalike page for the browser to fill in.
 * Nothing here can weaken that and nothing here should try to.
 *
 * ── WHY `@simplewebauthn/browser` AND NOT HAND-ROLLED ────────────────────────────
 *
 * The WebAuthn API takes and returns `ArrayBuffer`s where the server speaks
 * base64url — challenge, user id, credential ids, the whole attestation object.
 * Getting that conversion subtly wrong produces a ceremony that fails in the
 * browser with `SecurityError` or, worse, one that succeeds on the developer's
 * laptop and fails on a phone. `startRegistration`/`startAuthentication` are the
 * reference implementation of exactly that conversion, and they are what
 * `@better-auth/passkey` itself uses on the other side.
 *
 * Deliberately NOT the Better Auth CLIENT, though: the shell talks to `/identity/*`
 * with plain `fetch` everywhere else (`auth/identity.ts`, `auth/signIn.ts`), and
 * pulling a second HTTP client into the bundle to call two endpoints would be a
 * dependency earning nothing. The ceremony is the hard part; the two POSTs are not.
 */
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { API_BASE_URL } from "@/api/base";
import { IDENTITY_BASE } from "./identity";
import type { FetchLike } from "./http";

const PASSKEY_BASE = `${IDENTITY_BASE}/passkey`;
export const AUTH_OPTIONS_PATH = `${PASSKEY_BASE}/generate-authenticate-options`;
export const AUTH_VERIFY_PATH = `${PASSKEY_BASE}/verify-authentication`;
export const REGISTER_OPTIONS_PATH = `${PASSKEY_BASE}/generate-register-options`;
export const REGISTER_VERIFY_PATH = `${PASSKEY_BASE}/verify-registration`;
export const LIST_PATH = `${PASSKEY_BASE}/list-user-passkeys`;

/** The ceremony waits on a person finding their phone, so it is longer than a sign-in. */
const CEREMONY_TIMEOUT_MS = 120_000;

export type PasskeyOutcome =
  | { ok: true }
  /**
   * 🔴 `cancelled` is NOT an error, and separating it is the whole reason this is a
   * union rather than a boolean. Dismissing the browser's prompt is the single most
   * common way this flow ends — somebody changes their mind, or the phone is in the
   * other room — and showing "passkey sign-in failed" in red for it teaches people
   * that the feature is broken. The screen says nothing at all for this one.
   */
  | { ok: false; cancelled: true }
  | { ok: false; cancelled: false; message: string };

const cancelled: PasskeyOutcome = { ok: false, cancelled: true };
const failed = (message: string): PasskeyOutcome => ({ ok: false, cancelled: false, message });

/**
 * Whether this browser can do WebAuthn at all.
 *
 * 🔴 Asked BEFORE the button is drawn, never after it is pressed. An affordance that
 * appears and then explains it cannot work is worse than one that was never offered.
 */
export const passkeysSupported = (): boolean =>
  typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";

/**
 * 🔴 The browser throws `NotAllowedError` for BOTH a deliberate dismissal and a
 * timeout, and does so on purpose: distinguishing them would tell a hostile page
 * whether a credential existed. We honour that reading — both are `cancelled`.
 * `AbortError` is the page itself having moved on.
 */
const isCancellation = (error: unknown): boolean => {
  const name = (error as { name?: string } | null)?.name ?? "";
  return name === "NotAllowedError" || name === "AbortError";
};

/**
 * 🔴 **THREE OF THESE ROUTES ARE `GET`, AND SHIPPING THEM AS `POST` 404'd THE WHOLE
 * FEATURE.**
 *
 * `@better-auth/passkey` registers the two *option* endpoints and the list endpoint as
 * GET — they take no body, only optional query parameters — and the two *verify*
 * endpoints as POST. Better Auth routes on method as well as path, so a POST to a GET
 * route is simply not a route: it answers 404 from inside the library, with nothing in
 * our own log, because `identity.ts`'s allow-list had already let it through.
 *
 * It reached production. Every unit test mocked THIS MODULE rather than the fetch
 * underneath it, so the wire was never exercised — the same shape of mistake as the
 * `links.payload` bug, where the fake agreed with the code. `__tests__/passkeyWire.test.ts`
 * now asserts the method and path of every call for exactly this reason.
 */
type Wire = { method: "GET"; query?: Record<string, string> } | { method: "POST"; body: unknown };

async function call(path: string, wire: Wire, fetchImpl?: FetchLike): Promise<Response> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), CEREMONY_TIMEOUT_MS);
  const search =
    wire.method === "GET" && wire.query !== undefined && Object.keys(wire.query).length > 0
      ? `?${new URLSearchParams(wire.query).toString()}`
      : "";
  try {
    return await (fetchImpl ?? fetch)(`${API_BASE_URL}${path}${search}`, {
      method: wire.method,
      // 🔴 The session cookie is what registration is authorised by, and what the
      // verified authentication comes back as. Omitting it succeeds at the ceremony
      // and then leaves the person no more signed in than before.
      credentials: "include",
      signal: abort.signal,
      ...(wire.method === "POST"
        ? { headers: { "content-type": "application/json" }, body: JSON.stringify(wire.body ?? {}) }
        : {}),
    });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Sign in with a passkey. No address, no password — the authenticator names the person.
 *
 * ⚠️ **Must be called from a user gesture.** Browsers refuse `navigator.credentials.get()`
 * outside a click, so this cannot be moved into `onMounted` to "offer it automatically".
 */
export async function signInWithPasskey(fetchImpl?: FetchLike): Promise<PasskeyOutcome> {
  try {
    const options = await call(AUTH_OPTIONS_PATH, { method: "GET" }, fetchImpl);
    if (!options.ok) return failed("could not start passkey sign-in");

    const assertion = await startAuthentication({ optionsJSON: await options.json() });

    const verified = await call(AUTH_VERIFY_PATH, { method: "POST", body: { response: assertion } }, fetchImpl);
    if (!verified.ok) return failed("that passkey was not accepted");
    return { ok: true };
  } catch (error) {
    if (isCancellation(error)) return cancelled;
    return failed("passkey sign-in could not be completed");
  }
}

/**
 * Add a passkey to the account this browser is already signed in to.
 *
 * 🔴 Registration REQUIRES a live session, deliberately: a passkey is a new way into
 * an account, so proving you are already in is the only thing standing between it
 * and "anybody can add their own key to your account".
 */
/**
 * 🔴 **`name` IS WEBAUTHN'S `user.name`, NOT A LABEL** (PNK-149). Whatever is passed here is sent as
 * the account identifier the OPERATING SYSTEM shows in its own passkey list, and it is also stored
 * as the row's name. `Security.vue` passed today's date, so a real enrolment wrote `"9/16/2026"`
 * into the person's password manager — which we cannot correct there afterwards. Pass `null` and
 * Better Auth uses the account's e-mail, which is what the field means; the Pinksy-side label is
 * derived at read time instead (`auth/authenticatorName.ts`).
 */
export async function registerPasskey(name: string | null, fetchImpl?: FetchLike): Promise<PasskeyOutcome> {
  try {
    const options = await call(REGISTER_OPTIONS_PATH, { method: "GET", query: name === null ? {} : { name } }, fetchImpl);
    if (!options.ok) return failed("could not start passkey setup");

    const attestation = await startRegistration({ optionsJSON: await options.json() });

    // 🔴 **OMITTED, NEVER `null`** (PNK-149). The plugin's body schema is
    // `name: z.string().trim().optional()`, and `optional()` admits `undefined` — NOT `null`. Sending
    // the key with a null value fails validation, so the whole enrolment dies at 400 AFTER the person
    // has already touched their sensor and their password manager has already saved the credential.
    // A key that is absent and a key that is present-and-null are different things to a validator,
    // which is exactly the distinction `{ ...(x !== null && { x }) }` exists to preserve.
    const verified = await call(
      REGISTER_VERIFY_PATH,
      { method: "POST", body: { response: attestation, ...(name !== null ? { name } : {}) } },
      fetchImpl,
    );
    if (!verified.ok) return failed("that passkey could not be saved");
    return { ok: true };
  } catch (error) {
    if (isCancellation(error)) return cancelled;
    // The browser refuses a second key from the SAME authenticator for the same
    // account, which is correct and is not a failure worth alarming somebody about.
    if ((error as { name?: string } | null)?.name === "InvalidStateError") {
      return failed("this device already has a passkey for your account");
    }
    return failed("that passkey could not be saved");
  }
}

export interface SavedPasskey {
  id: string;
  name: string | null;
  createdAt: string | null;
  /** 🔴 Which authenticator this key lives in — what `authenticatorName.ts` names it by (PNK-149). */
  aaguid: string | null;
}

/** The person's own keys, for a screen that lists them. Never anybody else's. */
export async function listPasskeys(fetchImpl?: FetchLike): Promise<SavedPasskey[]> {
  try {
    const response = await call(LIST_PATH, { method: "GET" }, fetchImpl);
    if (!response.ok) return [];
    const body = (await response.json()) as unknown;
    const rows = Array.isArray(body) ? body : ((body as { passkeys?: unknown[] })?.passkeys ?? []);
    return (rows as Record<string, unknown>[]).map((row) => ({
      id: String(row.id ?? ""),
      name: typeof row.name === "string" && row.name.trim() !== "" ? row.name.trim() : null,
      createdAt: typeof row.createdAt === "string" ? row.createdAt : null,
      aaguid: typeof row.aaguid === "string" && row.aaguid.trim() !== "" ? row.aaguid.trim() : null,
    }));
  } catch {
    return [];
  }
}
